import type { Client } from '@snapshots/transport';

/**
 * The names of the people a page mentions (authors, who deleted or cancelled a snapshot), asked of the
 * service in one batch (`GET /v1/people`), which reads Beyond Accounts' directory with its own credential for
 * the people the person's snapshots name. An account is asked for once; one nobody named stays unnamed, and the
 * page says who it is without a name ("A member of …"). While the directory does not answer, only the person's
 * own name is known.
 */
export class People {
	static WAIT = 20;
	static MOST = 50;

	#client: Client;
	#known = new Map<string, string | null>();
	#waiting = new Set<string>();
	#listeners = new Set<() => void>();
	#timer?: ReturnType<typeof setTimeout>;

	constructor(client: Client) {
		this.#client = client;
	}

	/** The name of an account, when known; the first time, it is asked for */
	name(account: string | undefined): string | undefined {
		if (!account) return undefined;
		if (!this.#known.has(account)) this.#ask(account);
		return this.#known.get(account) ?? undefined;
	}

	/** Hears every time names are learned. @returns What stops listening */
	listen = (listener: () => void): (() => void) => {
		this.#listeners.add(listener);
		return () => void this.#listeners.delete(listener);
	};

	#ask(account: string): void {
		this.#known.set(account, null);
		this.#waiting.add(account);
		this.#timer ??= setTimeout(() => void this.#read(), People.WAIT);
	}

	async #read(): Promise<void> {
		this.#timer = undefined;
		const accounts = [...this.#waiting].slice(0, People.MOST);
		for (const account of accounts) this.#waiting.delete(account);
		if (this.#waiting.size) this.#timer = setTimeout(() => void this.#read(), People.WAIT);
		try {
			const answer = await this.#client.get<{ people: { account: string; name: string }[] }>(`/v1/people?${new URLSearchParams({ accounts: accounts.join(',') })}`);
			let learned = false;
			for (const person of answer.people ?? []) {
				if (accounts.includes(person.account) && person.name) {
					this.#known.set(person.account, person.name);
					learned = true;
				}
			}
			if (learned) for (const listener of this.#listeners) listener();
		} catch {
			// Not answered: they are asked for again the next time a page names them
			for (const account of accounts) this.#known.delete(account);
		}
	}
}
